import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { lazy, startTransition, Suspense, useEffect } from "react";
import { Route, Switch, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import {
  getInternalPath,
  getPrefetchPath,
  prefetchRoute,
} from "./routePrefetch";

const Home = lazy(() => import("./pages/Home"));
const FornecedoresGate = lazy(() => import("./pages/FornecedoresGate"));
const Anunciante = lazy(() => import("./pages/Anunciante"));
const PublicAdvertiser = lazy(() => import("./pages/PublicAdvertiser"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminGuard = lazy(() =>
  import("./pages/Admin").then(module => ({ default: module.AdminGuard }))
);
const AdminAnuncios = lazy(() => import("./pages/AdminAnuncios"));
const AdminMensagens = lazy(() => import("./pages/AdminMensagens"));
const AdminMembros = lazy(() => import("./pages/AdminMembros"));
const AdminAnunciantes = lazy(() => import("./pages/AdminAnunciantes"));
const AdminNoticias = lazy(() => import("./pages/AdminNoticias"));
const AdminConteudo = lazy(() => import("./pages/AdminConteudo"));
const AdminAcademy = lazy(() => import("./pages/AdminAcademy"));
const LoginCadastro = lazy(() => import("./pages/LoginCadastro"));
const Assinatura = lazy(() => import("./pages/Assinatura"));
const CheckoutAssinatura = lazy(() => import("./pages/CheckoutAssinatura"));
const Conteudo = lazy(() => import("./pages/Conteudo"));
const Contato = lazy(() => import("./pages/Contato"));
const AnunciePlanos = lazy(() => import("./pages/AnunciePlanos"));
const AnuncieCheckout = lazy(() => import("./pages/AnuncieCheckout"));
const AnuncieCadastro = lazy(() => import("./pages/AnuncieCadastro"));
const MapaLimeira = lazy(() => import("./pages/MapaLimeira"));
const HoteisPousadas = lazy(() => import("./pages/HoteisPousadas"));
const PrimeClubBeneficios = lazy(() => import("./pages/PrimeClubBeneficios"));
const PrimeClubCard = lazy(() => import("./pages/PrimeClubCard"));
const PrimeValidation = lazy(() => import("./pages/PrimeValidation"));
const HappApps = lazy(() => import("./pages/HappApps"));
const Noticias = lazy(() => import("./pages/Noticias"));
const QuemSomos = lazy(() => import("./pages/QuemSomos"));
const Academy = lazy(() => import("./pages/Academy"));
const AcademyCourseDetail = lazy(() => import("./pages/AcademyCourseDetail"));
const AcademyStudentDashboard = lazy(
  () => import("./pages/AcademyStudentDashboard")
);
const AcademyCertificateValidation = lazy(
  () => import("./pages/AcademyCertificateValidation")
);
const PortalUtilityDock = lazy(() => import("./components/PortalUtilityDock"));

function Router() {
  return (
    <Switch>
      <Route path="/" component={Home} />
      <Route path="/categorias" component={FornecedoresGate} />
      <Route
        path="/fornecedores/hoteis-e-hospedagem"
        component={FornecedoresGate}
      />
      <Route path="/fornecedores/:categorySlug" component={FornecedoresGate} />
      <Route path="/fornecedores" component={FornecedoresGate} />
      <Route path="/anunciante/:id" component={PublicAdvertiser} />
      <Route path="/anunciante" component={Anunciante} />
      <Route path="/fabricante/:slug" component={() => <FornecedoresGate />} />
      <Route path="/admin" component={Admin} />
      <Route
        path="/admin/anuncios"
        component={() => (
          <AdminGuard allowedRoles={["admin", "moderator"]}>
            <AdminAnuncios />
          </AdminGuard>
        )}
      />
      <Route
        path="/admin/mensagens"
        component={() => (
          <AdminGuard allowedRoles={["admin", "commercial"]}>
            <AdminMensagens />
          </AdminGuard>
        )}
      />
      <Route
        path="/admin/membros"
        component={() => (
          <AdminGuard allowedRoles={["admin"]}>
            <AdminMembros />
          </AdminGuard>
        )}
      />
      <Route
        path="/admin/anunciantes"
        component={() => (
          <AdminGuard allowedRoles={["admin", "commercial", "moderator"]}>
            <AdminAnunciantes />
          </AdminGuard>
        )}
      />
      <Route
        path="/admin/noticias"
        component={() => (
          <AdminGuard allowedRoles={["admin", "moderator"]}>
            <AdminNoticias />
          </AdminGuard>
        )}
      />
      <Route
        path="/admin/conteudo"
        component={() => (
          <AdminGuard allowedRoles={["admin"]}>
            <AdminConteudo />
          </AdminGuard>
        )}
      />
      <Route
        path="/admin/academy"
        component={() => (
          <AdminGuard allowedRoles={["admin", "moderator"]}>
            <AdminAcademy />
          </AdminGuard>
        )}
      />
      <Route path="/login" component={() => <LoginCadastro />} />
      <Route path="/cadastro" component={() => <LoginCadastro />} />
      <Route path="/checkout" component={CheckoutAssinatura} />
      <Route path="/assinatura" component={Assinatura} />
      <Route path="/prime-club" component={PrimeClubBeneficios} />
      <Route path="/prime-carteirinha" component={PrimeClubCard} />
      <Route path="/prime-validar" component={PrimeValidation} />
      <Route path="/tecnologia-happ-apps" component={HappApps} />
      <Route path="/quem-somos" component={QuemSomos} />
      <Route path="/academy" component={Academy} />
      <Route path="/academy/aluno" component={AcademyStudentDashboard} />
      <Route path="/academy/validar" component={AcademyCertificateValidation} />
      <Route path="/academy/curso/:slug" component={AcademyCourseDetail} />
      <Route path="/descobrir" component={Conteudo} />
      <Route path="/noticias/:slug" component={Noticias} />
      <Route path="/noticias" component={Noticias} />
      <Route path="/programas" component={Conteudo} />
      <Route path="/anuncie/checkout" component={AnuncieCheckout} />
      <Route path="/anuncie/cadastro" component={AnuncieCadastro} />
      <Route path="/anuncie" component={AnunciePlanos} />
      <Route path="/contato" component={Contato} />
      <Route path="/mapa" component={MapaLimeira} />
      <Route path="/pousada-recanto-portugues" component={HoteisPousadas} />
      <Route path="/hoteis-pousadas" component={FornecedoresGate} />
      <Route path="/404" component={NotFound} />
      <Route component={NotFound} />
    </Switch>
  );
}

function NavigationPerformance() {
  const [, navigate] = useLocation();

  useEffect(() => {
    const prefetchedPaths = new Set<string>();

    const prefetchFromTarget = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest<HTMLAnchorElement>("a[href]");
      if (
        !anchor ||
        anchor.target === "_blank" ||
        anchor.hasAttribute("download")
      ) {
        return;
      }

      const path = getPrefetchPath(anchor.href);
      if (!path || prefetchedPaths.has(path)) return;
      prefetchedPaths.add(path);
      prefetchRoute(path);
    };

    const handleClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest<HTMLAnchorElement>("a[href]");
      if (
        !anchor ||
        anchor.target === "_blank" ||
        anchor.hasAttribute("download")
      ) {
        return;
      }

      const path = getInternalPath(anchor.href);
      if (!path) return;

      const currentPathWithoutHash = `${window.location.pathname}${window.location.search}`;
      if (path.split("#", 1)[0] === currentPathWithoutHash) return;

      event.preventDefault();
      startTransition(() => navigate(path));
    };

    document.addEventListener("pointerover", prefetchFromTarget, true);
    document.addEventListener("pointerdown", prefetchFromTarget, true);
    document.addEventListener("focusin", prefetchFromTarget, true);
    document.addEventListener("click", handleClick, true);

    return () => {
      document.removeEventListener("pointerover", prefetchFromTarget, true);
      document.removeEventListener("pointerdown", prefetchFromTarget, true);
      document.removeEventListener("focusin", prefetchFromTarget, true);
      document.removeEventListener("click", handleClick, true);
    };
  }, [navigate]);

  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <Toaster />
          <NavigationPerformance />
          <Suspense
            fallback={
              <div
                className="route-loading"
                role="status"
                aria-label="Abrindo página"
              >
                <span className="route-loading-bar" aria-hidden="true" />
              </div>
            }
          >
            <Router />
          </Suspense>
          <Suspense fallback={null}>
            <PortalUtilityDock />
          </Suspense>
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
